import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, XCircle, CheckCircle } from "@phosphor-icons/react";

export default function ConsentRevokePage() {
  const [params] = useSearchParams();
  const lead_id = params.get("lead_id") || "";
  const subscriber_id = params.get("subscriber_id") || "";
  const token = params.get("token") || "";
  const slug = params.get("slug") || "";
  const mode = subscriber_id ? "newsletter" : "lead";
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    if (!slug) return;
    api.get(`/tenants/public/${slug}`).then((r) => setTenant(r.data)).catch(() => {});
  }, [slug]);

  const submit = async () => {
    if (!confirmed) {
      setStatus({ ok: false, msg: "Bitte den Widerruf bestätigen." });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      let r;
      if (mode === "newsletter") {
        r = await api.post("/public/newsletter/unsubscribe", { subscriber_id, token, reason });
      } else {
        r = await api.post("/public/consent/revoke", { lead_id, token, reason });
      }
      setStatus({
        ok: true,
        msg: r.data.already_revoked || r.data.already_unsubscribed
          ? "Ihre Abmeldung war bereits registriert — es sind keine weiteren Schritte nötig."
          : (mode === "newsletter"
             ? "Sie wurden aus dem Newsletter ausgetragen. Vielen Dank."
             : "Ihr Widerruf wurde bestätigt. Eine Bestätigungs-E-Mail wurde an Sie versendet."),
      });
    } catch (e) {
      setStatus({ ok: false, msg: e.response?.data?.detail || "Widerruf fehlgeschlagen. Bitte prüfen Sie den Link." });
    } finally {
      setBusy(false);
    }
  };

  if (!token || (!lead_id && !subscriber_id)) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-black/10 rounded-2xl p-8 text-center" data-testid="revoke-page-invalid">
          <XCircle size={40} weight="duotone" className="mx-auto text-red-500 mb-3" />
          <h1 className="font-heading text-xl font-bold mb-2">Ungültiger Widerruf-Link</h1>
          <p className="text-sm text-zinc-600">Der Link enthält keine gültigen Parameter. Bitte nutzen Sie den Link aus unserer E-Mail.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="revoke-page">
      <div className="max-w-lg w-full bg-white border border-black/10 rounded-2xl p-8 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheck size={28} weight="duotone" className="text-emerald-600" />
          <h1 className="font-heading text-2xl font-bold tracking-tight">
            {mode === "newsletter" ? "Newsletter abbestellen" : "Datenschutz-Widerruf"}
          </h1>
        </div>
        <p className="text-sm text-zinc-700 leading-relaxed mb-2">
          {mode === "newsletter" ? (
            <>Sie können hier den Newsletter von <strong>{tenant?.name || "diesem Anbieter"}</strong> jederzeit kostenlos abbestellen.</>
          ) : (
            <>Sie können hier Ihre Einwilligung in die Verarbeitung Ihrer personenbezogenen Daten bei <strong>{tenant?.name || "diesem Anbieter"}</strong> widerrufen.</>
          )}
        </p>
        <p className="text-xs text-zinc-500 mb-6">
          {mode === "newsletter"
            ? "Nach der Abmeldung erhalten Sie keine Newsletter-E-Mails mehr. Falls Sie zusätzlich als Kunde bei uns registriert sind, bleibt Ihr Kundenkonto erhalten."
            : "Ihre Interessenten-Daten (Name, E-Mail, Adresse) werden anonymisiert und Sie werden aus dem Newsletter ausgetragen. Gesetzliche Aufbewahrungspflichten (z. B. laufende Verträge, Rechnungen) bleiben unberührt."
          }
        </p>

        {!status?.ok && (
          <>
            <label className="block text-xs font-semibold text-zinc-700 mb-1">Grund (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={400}
              placeholder="z. B. Kein Interesse mehr, doppelte Registrierung, …"
              className="w-full px-3 py-2 border border-black/10 rounded-lg text-sm mb-4"
              data-testid="revoke-reason"
            />

            <label className="flex items-start gap-2 text-sm text-zinc-700 cursor-pointer mb-5">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-0.5 w-5 h-5"
                data-testid="revoke-confirm"
              />
              <span>Ich bestätige, dass ich meine Datenschutz-Einwilligung ausdrücklich und dauerhaft widerrufen möchte.</span>
            </label>

            <button
              onClick={submit}
              disabled={busy || !confirmed}
              className="w-full h-11 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50"
              data-testid="revoke-submit"
            >
              {busy ? "Verarbeite…" : "Widerruf jetzt einreichen"}
            </button>
          </>
        )}

        {status && (
          <div
            className={`mt-5 p-4 rounded-lg text-sm ${status.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-800 border border-red-200"}`}
            data-testid="revoke-feedback"
          >
            <div className="flex items-start gap-2">
              {status.ok ? <CheckCircle size={18} weight="bold" className="mt-0.5" /> : <XCircle size={18} weight="bold" className="mt-0.5" />}
              <div>{status.msg}</div>
            </div>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-black/5 text-[11px] text-zinc-400 font-mono">
          Referenz: {mode === "newsletter" ? subscriber_id : lead_id}
        </div>
      </div>
    </div>
  );
}
